/**
 * The deposit of an accepted request (specification §4.3).
 *
 * Everyone who sees the request sees the deposit's deadline and, folded away, its parameters,
 * which the manifest fixes. The brand also gets the deposit itself: one button that approves
 * exactly the budget and creates the campaign from its wallet (see `campaigns/deposit.ts`), which
 * is never offered while a deposit this browser sent is still on its way. Once Morcast has
 * recorded the deposit from the chain, the request is funded and the section shows its campaign
 * instead, with a link to its public page.
 *
 * Deposits that carry the request's manifest hash but fund nothing are listed apart (see
 * `UnmatchedDeposits`), with what becomes of their money.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useConfig, useConnection } from "wagmi";

import { BrandFunds } from "../../campaigns/BrandFunds.js";
import { campaignPath } from "../../campaigns/campaigns.js";
import {
  DEPOSIT_STEP_MESSAGES,
  DepositError,
  type DepositStep,
  describeDepositError,
  forgetSentDeposit,
  isAwaited,
  makeDeposit,
  RECORDING_PATIENCE_MS,
  rememberSentDeposit,
  type SentDeposit,
  sentDepositOf,
  useSentDeposit,
} from "../../campaigns/deposit.js";
import {
  type Campaign,
  type CampaignRequest,
  type Deposit,
  pendingChange,
  pendingTransfer,
  REQUESTS_KEY,
} from "../../campaigns/requests.js";
import { AddressLink, TransactionLink } from "../../components/ChainLinks.js";
import { SignInDialog } from "../../components/SignInDialog.js";
import { SupportNote } from "../../components/SupportNote.js";
import { Disclosure, Fact, Notice, styles } from "../../components/ui.js";
import { formatAmount, formatCount, formatUtc } from "../../format.js";

export function DepositSection({
  request,
  view,
}: {
  request: CampaignRequest;
  view: "brand" | "admin";
}) {
  // A deposit this browser sent need not be kept once the request shows what became of it: the
  // request moved on, or the deposit is among those that fund nothing.
  useEffect(() => {
    const sent = sentDepositOf(request.id);
    if (
      request.status !== "accepted" ||
      request.unmatchedDeposits.some((deposit) => deposit.createdTx === sent?.transactionHash)
    ) {
      forgetSentDeposit(request.id);
    }
  }, [request.id, request.status, request.unmatchedDeposits]);

  const { deposit } = request;
  if (!deposit) {
    return null;
  }
  return (
    <section
      id="deposit"
      aria-labelledby="deposit-title"
      className={`mt-6 scroll-mt-24 ${styles.card}`}
    >
      <h2 id="deposit-title" className="text-lg font-semibold tracking-tight">
        Deposit
      </h2>
      {request.campaign ? (
        <>
          <CampaignFacts campaign={request.campaign} />
          {view === "brand" && <BrandFunds campaign={request.campaign} token={request.token} />}
        </>
      ) : (
        <DepositStatus request={request} deposit={deposit} view={view} />
      )}
      <div className="mt-5">
        <Disclosure summary="Deposit parameters">
          <p className="text-zinc-600 dark:text-zinc-400">
            The values the deposit sends to the escrow, fixed by the accepted terms. Your wallet
            sends them for you, and anyone can check them against the manifest.
          </p>
          <DepositParameters request={request} deposit={deposit} />
        </Disclosure>
      </div>
    </section>
  );
}

/** What the escrow's campaign status means for the brand's budget. */
const CAMPAIGN_STATUS: Record<Campaign["status"], string> = {
  funded: "Funded. The budget is in the escrow.",
  cancelled: "Cancelled before the start. The escrow returned the budget.",
  settled: "Settled. Creators claim their payouts from the escrow.",
  refunded: "Refunded. The escrow returned the budget.",
};

function CampaignFacts({ campaign }: { campaign: Campaign }) {
  return (
    <>
      <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
        {CAMPAIGN_STATUS[campaign.status]}
      </p>
      {/* A campaign cancelled before its start never ran, so it has no public page. */}
      {campaign.status !== "cancelled" && (
        <Link to={campaignPath(campaign)} className={`mt-3 inline-block text-sm ${styles.link}`}>
          Open the campaign page
        </Link>
      )}
      <dl className="mt-4 grid gap-x-6 gap-y-4 text-sm sm:grid-cols-3">
        <Fact label="Campaign">{campaign.id}</Fact>
        <Fact label="Created">{formatUtc(campaign.createdAt)}</Fact>
        <Fact label="Transaction">
          <TransactionLink chainId={campaign.chainId} hash={campaign.createdTx} />
        </Fact>
      </dl>
    </>
  );
}

function DepositStatus({
  request,
  deposit,
  view,
}: {
  request: CampaignRequest;
  deposit: Deposit;
  view: "brand" | "admin";
}) {
  if (request.status === "lapsed") {
    return (
      <Notice tone="warning" className="mt-3">
        No deposit arrived before the start, and the escrow refuses deposits from then on, so the
        request lapsed.
        {/* The brand's next step above says how to run it. Admins learn what the brand can do. */}
        {view === "admin" &&
          " The brand can ask for new dates, which the admin team reviews as a change."}
      </Notice>
    );
  }
  return (
    <>
      <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
        {view === "brand" ? "Deposit" : "The brand deposits"}{" "}
        {formatAmount(deposit.budget, request.token)} into the escrow before{" "}
        <strong>{formatUtc(deposit.depositBefore)}</strong>. The escrow refuses deposits from the
        start on.
      </p>
      <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-zinc-600 dark:text-zinc-400">
        <li>The wallet approves the escrow to take exactly the budget.</li>
        <li>It then creates the campaign with the accepted terms.</li>
        <li>Morcast records the deposit from the chain, usually within a minute.</li>
      </ol>
      <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
        Wallets that send several calls at once, such as Base Account, ask for one confirmation.
      </p>
      {view === "brand" && request.status === "accepted" && (
        <DepositAction request={request} deposit={deposit} />
      )}
    </>
  );
}

/**
 * The brand's deposit: the button, its progress, and the deposit once the wallet sent it. The
 * button is offered while the deposit window is open and the wallet is connected. The deposit
 * itself checks that the wallet is the request's (see `makeDeposit`).
 */
function DepositAction({ request, deposit }: { request: CampaignRequest; deposit: Deposit }) {
  const config = useConfig();
  const { isConnected } = useConnection();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<DepositStep | null>(null);
  const sent = useSentDeposit(request.id);
  const [signingIn, setSigningIn] = useState(false);
  const windowOpen = Date.now() < Date.parse(deposit.depositBefore);

  const make = useMutation({
    mutationFn: () =>
      makeDeposit(config, request, setStep, (transactionHash) =>
        // Kept from now on, whatever happens to this page: the deposit may land.
        rememberSentDeposit(request.id, {
          transactionHash,
          campaignId: null,
          sentAt: Date.now(),
          unconfirmed: true,
        }),
      ),
    onSuccess: (made) => {
      rememberSentDeposit(request.id, {
        transactionHash: made.transactionHash,
        campaignId: made.campaignId?.toString() ?? null,
        sentAt: sentDepositOf(request.id)?.sentAt ?? Date.now(),
      });
      // The request is read again until it shows the campaign (see `useRequest`).
      void queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
    },
    onError: (error) => {
      // Nothing was deposited, so the brand may try again. Any other error after the wallet sent
      // the deposit leaves it awaited, because it may still land.
      if (error instanceof DepositError) {
        forgetSentDeposit(request.id);
      }
    },
    onSettled: () => setStep(null),
  });

  if (sent) {
    return (
      <SentDepositNotice
        chainId={deposit.chainId}
        sent={sent}
        waiting={make.isPending}
        {...(windowOpen ? { onDepositAgain: () => forgetSentDeposit(request.id) } : {})}
      />
    );
  }
  if (!windowOpen) {
    return (
      <Notice tone="warning" className="mt-4">
        The deposit window closed at {formatUtc(deposit.depositBefore)}.
      </Notice>
    );
  }
  // The request is offered to another wallet, which deposits once it takes it over (D26).
  const move = pendingTransfer(request);
  if (move) {
    return (
      <Notice tone="info" className="mt-4">
        The deposit waits for the move, because the other wallet deposits once it takes the request
        over. Cancel the move to deposit from this wallet.
      </Notice>
    );
  }
  // A change waiting for review may give the request new deposit parameters (D25).
  if (pendingChange(request)) {
    return (
      <Notice tone="info" className="mt-4">
        You asked to change the terms. Deposit once Morcast has decided the change, or withdraw the
        change to deposit with the current terms.
      </Notice>
    );
  }
  if (!isConnected) {
    return (
      <div className="mt-4 text-sm">
        <Notice tone="info">Your wallet is not connected. Sign in with it again to deposit.</Notice>
        <button
          type="button"
          onClick={() => setSigningIn(true)}
          className={`mt-3 ${styles.primaryButton}`}
        >
          Sign in
        </button>
        <SignInDialog open={signingIn} onClose={() => setSigningIn(false)} />
      </div>
    );
  }
  return (
    <div className="mt-4">
      <button
        type="button"
        disabled={make.isPending}
        onClick={() => make.mutate()}
        className={styles.primaryButton}
      >
        Deposit {formatAmount(deposit.budget, request.token)}
      </button>
      <div aria-live="polite" className="mt-3 min-h-5 text-sm">
        {make.isPending && (
          <p className="text-zinc-600 dark:text-zinc-400">
            {step ? DEPOSIT_STEP_MESSAGES[step] : "Checking the deposit…"}
          </p>
        )}
        {make.isError && (
          <>
            <Notice tone="error">{describeDepositError(make.error)}</Notice>
            <SupportNote className="mt-2" />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * A deposit this browser sent: on its way, mined, or overdue. An overdue deposit may never have
 * gone through, so the brand can deposit again while the window is open. A deposit that arrives
 * twice funds nothing and is voided, which returns it.
 */
function SentDepositNotice({
  chainId,
  sent,
  waiting,
  onDepositAgain,
}: {
  chainId: number;
  sent: SentDeposit;
  /** Whether this page still waits for the deposit to be mined. */
  waiting: boolean;
  onDepositAgain?: () => void;
}) {
  const transaction = sent.transactionHash && (
    <>
      {" "}
      in transaction <TransactionLink chainId={chainId} hash={sent.transactionHash} />
    </>
  );
  if (!waiting && !isAwaited(sent)) {
    const check = sent.transactionHash ? "Check the transaction" : "Check your wallet's activity";
    return (
      <div className="mt-4">
        <Notice tone="warning">
          The deposit{transaction} has not reached Morcast in {RECORDING_PATIENCE_MS / 60_000}{" "}
          minutes.{" "}
          {onDepositAgain
            ? `${check}: if the deposit failed or never went through, deposit again. Morcast voids a deposit that arrives twice, which returns it to your wallet.`
            : `${check} to see whether it went through.`}
        </Notice>
        {onDepositAgain && (
          <button
            type="button"
            onClick={onDepositAgain}
            className={`mt-3 ${styles.secondaryButton}`}
          >
            Deposit again
          </button>
        )}
      </div>
    );
  }
  if (sent.unconfirmed) {
    return (
      <Notice tone="info" className="mt-4">
        Your wallet sent the deposit{transaction}.{" "}
        {waiting
          ? "Waiting for it to be mined…"
          : "Once it is mined, Morcast records it, usually within a minute. This page updates by itself."}
      </Notice>
    );
  }
  return (
    <Notice tone="success" className="mt-4">
      Deposited{transaction}
      {sent.campaignId && <>, campaign {sent.campaignId}</>}. Morcast records it within a minute,
      and this page updates by itself.
    </Notice>
  );
}

/**
 * Deposits with the request's manifest hash that fund nothing (see `UnmatchedDeposit`), each with
 * what became of its money: Morcast voids it, which returns it to the wallet that sent it.
 */
export function UnmatchedDeposits({ request }: { request: CampaignRequest }) {
  const deposits = request.unmatchedDeposits;
  if (deposits.length === 0) {
    return null;
  }
  return (
    <Notice
      tone="warning"
      title={deposits.length === 1 ? "A deposit that funds nothing" : "Deposits that fund nothing"}
      className="mt-6"
    >
      <ul className="space-y-2">
        {deposits.map((deposit) => (
          <li key={`${deposit.escrow}/${deposit.id}`}>
            {formatAmount(deposit.budget, deposit.token)} from{" "}
            <AddressLink chainId={deposit.chainId} address={deposit.brand} short /> in transaction{" "}
            <TransactionLink chainId={deposit.chainId} hash={deposit.createdTx} />,{" "}
            {formatUtc(deposit.createdAt)}. {deposit.reason}{" "}
            {deposit.status === "funded"
              ? "Morcast voids it, which returns all of it to that wallet."
              : "The escrow has returned it to that wallet."}
          </li>
        ))}
      </ul>
    </Notice>
  );
}

/** The exact `createCampaign` parameters, as the manifest fixes them. */
function DepositParameters({ request, deposit }: { request: CampaignRequest; deposit: Deposit }) {
  return (
    <dl className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2">
      <Fact label="Escrow">
        <AddressLink chainId={deposit.chainId} address={deposit.escrow} />
      </Fact>
      <Fact label="Token">
        <span className="font-mono text-xs">{deposit.token}</span>
      </Fact>
      <Fact label="Budget">
        {formatAmount(deposit.budget, request.token)}{" "}
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          ({deposit.budget} base units)
        </span>
      </Fact>
      <Fact label="Target">{formatCount(deposit.target)}</Fact>
      <Fact label="Start">
        {formatUtc(new Date(Number(deposit.startAt) * 1000).toISOString())}{" "}
        <span className="text-xs text-zinc-500 dark:text-zinc-400">({deposit.startAt})</span>
      </Fact>
      <Fact label="End">
        {formatUtc(new Date(Number(deposit.endAt) * 1000).toISOString())}{" "}
        <span className="text-xs text-zinc-500 dark:text-zinc-400">({deposit.endAt})</span>
      </Fact>
      <Fact label="Manifest hash">
        <a
          href={`/api/manifests/${deposit.manifestHash}`}
          className="font-mono text-xs break-all hover:underline"
        >
          {deposit.manifestHash}
        </a>
      </Fact>
    </dl>
  );
}
